import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { whatsappLink, WhatsAppOrigin } from '../lib/whatsapp';
import { useLanguage } from '../lib/i18n';
type Experience = {
  tagKey: string;
  titleKey: string;
  descriptionKey: string;
  benefitsKeys: string[];
  priceKey: string;
  image: string;
  waOrigin: WhatsAppOrigin;
  featured?: boolean;
  categoryTagsKeys?: string[];
  noteKey?: string;
};
const experiences: Experience[] = [{
  tagKey: 'experiences.items.sunset.tag',
  titleKey: 'experiences.items.sunset.title',
  descriptionKey: '',
  categoryTagsKeys: ['experiences.tags.romantic', 'experiences.tags.proposal', 'experiences.tags.anniversary'],
  benefitsKeys: ['experiences.items.sunset.b1', 'experiences.items.sunset.b2', 'experiences.items.sunset.b3', 'experiences.items.sunset.b4', 'experiences.items.sunset.b5', 'experiences.items.sunset.b6'],
  priceKey: 'experiences.items.sunset.price',
  noteKey: 'experiences.items.sunset.note',
  image: 'https://cdn.magicpatterns.com/uploads/e7eqxJpf3kJ33NgpVAL1TP/ChatGPT_Image_May_27,_2026_at_04_04_13_PM.png',
  waOrigin: 'brunch'
}, {
  tagKey: 'experiences.items.ocean.tag',
  titleKey: 'experiences.items.ocean.title',
  descriptionKey: '',
  categoryTagsKeys: ['experiences.tags.premium', 'experiences.tags.romantic', 'experiences.tags.proposal', 'experiences.tags.anniversary'],
  benefitsKeys: ['experiences.items.ocean.b1', 'experiences.items.ocean.b2', 'experiences.items.ocean.b3', 'experiences.items.ocean.b4', 'experiences.items.ocean.b5', 'experiences.items.ocean.b6'],
  priceKey: 'experiences.items.ocean.price',
  noteKey: 'experiences.items.ocean.note',
  image: 'https://cdn.magicpatterns.com/uploads/nLT1moJG76L7yJxyA3Uoxp/ChatGPT_Image_May_27,_2026_at_04_00_02_PM.png',
  waOrigin: 'sunset',
  featured: true
}, {
  tagKey: 'experiences.items.party.tag',
  titleKey: 'experiences.items.party.title',
  descriptionKey: '',
  categoryTagsKeys: ['experiences.tags.birthday', 'experiences.tags.anniversary'],
  benefitsKeys: ['experiences.items.party.b1', 'experiences.items.party.b2', 'experiences.items.party.b3', 'experiences.items.party.b4', 'experiences.items.party.b5', 'experiences.items.party.b6'],
  priceKey: 'experiences.items.party.price',
  noteKey: 'experiences.items.party.note',
  image: 'https://cdn.magicpatterns.com/uploads/fPza9fqwnnysq4LsayfSCf/ChatGPT_Image_May_27,_2026_at_04_44_31_PM.png',
  waOrigin: 'love'
}];
export function Experiences() {
  const {
    t,
    lang
  } = useLanguage();
  return <section id="experiencias" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-8 lg:px-24 bg-beige w-full overflow-hidden" data-id="element-67">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 1.2,
      ease: [0.22, 1, 0.36, 1]
    }} className="text-center max-w-3xl mx-auto mb-12 sm:mb-16 lg:mb-24" data-id="element-68">
        <p className="uppercase tracking-[0.3em] text-olive text-[9px] sm:text-[10px] font-semibold mb-4 sm:mb-6" data-id="element-69">
          {t('experiences.tag')}
        </p>
        <h2 className="font-display text-fluid-h2 text-volcanic font-light mb-6 sm:mb-8" data-id="element-70">
          {t('experiences.title1')} <br data-id="element-71" />
          <span className="font-script text-fluid-script text-terracotta inline-block mt-[-0.1em]" data-id="element-72">
            {t('experiences.title2')}
          </span>
        </h2>
        <p className="text-volcanic/80 leading-relaxed text-[14px] sm:text-[15px] max-w-xl mx-auto font-light" data-id="element-73">
          {t('experiences.desc')}
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 max-w-[1400px] mx-auto" data-id="element-74">
        {experiences.map((exp, index) => <motion.article key={exp.titleKey} initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 1.2,
        delay: index * 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className={`group relative overflow-hidden bg-ivory flex flex-col border border-sand w-full ${exp.featured ? 'md:-translate-y-6 shadow-md' : 'shadow-sm'}`} data-id="element-75">
            <div className="relative h-[280px] sm:h-[360px] lg:h-[480px] overflow-hidden" data-id="element-76">
              <img src={exp.image} alt={t(exp.titleKey)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2s] ease-out" data-id="element-77" />
              <div className="absolute inset-0 bg-gradient-to-t from-volcanic/70 via-volcanic/10 to-transparent" data-id="element-78" />
              <div className="absolute top-4 sm:top-6 left-4 sm:left-6 text-ivory text-[9px] sm:text-[10px] tracking-[0.2em] uppercase font-medium" data-id="element-79">
                {t(exp.tagKey)}
              </div>
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-ivory" data-id="element-80">
                <p className="font-display italic text-base sm:text-lg tracking-wide opacity-90" data-id="element-81">
                  {t(exp.priceKey)}
                </p>
              </div>
            </div>
            <div className="p-6 sm:p-8 lg:p-10 flex flex-col flex-1" data-id="element-82">
              {exp.categoryTagsKeys && exp.categoryTagsKeys.length > 0 && <div className="flex flex-wrap gap-2 mb-5 sm:mb-6" data-id="element-83">
                  {exp.categoryTagsKeys.map(tKey => <span key={tKey} className="bg-terracotta/10 text-terracotta uppercase tracking-[0.2em] text-[9px] sm:text-[10px] px-3 py-1.5 rounded-full font-medium" data-id="element-84">
                      {t(tKey)}
                    </span>)}
                </div>}
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-volcanic mb-3 sm:mb-4 leading-tight font-light" data-id="element-85">
                {t(exp.titleKey)}
              </h3>
              {exp.descriptionKey && <p className="text-[13px] sm:text-[14px] leading-relaxed text-volcanic/80 mb-6 sm:mb-8 font-light" data-id="element-86">
                  {t(exp.descriptionKey)}
                </p>}
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 text-[12px] sm:text-[13px] text-volcanic/80 font-light" data-id="element-87">
                {exp.benefitsKeys.map(bKey => <li key={bKey} className="flex items-start gap-3" data-id="element-88">
                    <span className="mt-1.5 sm:mt-2 w-1.5 h-1.5 rounded-full bg-olive flex-shrink-0" data-id="element-89" />
                    <span data-id="element-90">{t(bKey)}</span>
                  </li>)}
              </ul>
              {exp.noteKey && <p className="uppercase tracking-[0.2em] text-[10px] sm:text-[11px] text-volcanic/70 font-medium mb-6 sm:mb-8" data-id="element-91">
                  {t(exp.noteKey)}
                </p>}
              <a href={whatsappLink(exp.waOrigin, lang)} target="_blank" rel="noopener noreferrer" className="mt-auto inline-flex items-center justify-between gap-3 bg-terracotta hover:bg-terracottaDark text-ivory px-6 sm:px-8 py-3.5 sm:py-4 rounded-md uppercase tracking-[0.2em] text-[9px] sm:text-[10px] transition-colors duration-300 group/btn w-full" data-id="element-92">
                <span data-id="element-93">{t('experiences.book')}</span>
                <ArrowRightIcon size={14} strokeWidth={1.5} className="group-hover/btn:translate-x-1 transition-transform duration-300 sm:w-4 sm:h-4 w-3.5 h-3.5" data-id="element-94" />
              </a>
            </div>
          </motion.article>)}
      </div>

      <motion.div initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1.2,
      delay: 0.6
    }} className="text-center mt-12 sm:mt-16 flex flex-col items-center gap-3 sm:gap-4 px-4" data-id="element-95">
        <div className="w-10 sm:w-12 h-[1px] bg-terracotta/40" data-id="element-96" />
        <p className="text-volcanic/60 font-display italic text-lg sm:text-xl" data-id="element-97">
          {t('experiences.footer')}
        </p>
      </motion.div>
    </section>;
}